<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import DestructiveConfirmDialog from '~/components/app/DestructiveConfirmDialog.vue'
import DiscardUnsavedDialog from '~/components/app/DiscardUnsavedDialog.vue'
import EntityFormFooter from '~/components/Forms/EntityFormFooter.vue'
import EntityFormSkeleton from '~/components/Forms/EntityFormSkeleton.vue'
import SourceSelect from '~/components/ui/SourceSelect.vue'
import SourceInput from '~/components/ui/SourceInput.vue'
import SourceRichTextEditor from '~/components/ui/SourceRichTextEditor.vue'
import { useDomaines } from '~/composables/orga/useDomaine'
import { useConcurrentForm } from '~/composables/orga/useConcurrentForm'
import OrganizationConflictDialog from '~/components/Forms/OrganizationConflictDialog.vue'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import { getErrorMessage, normalizeString } from '~/composables/ui/useFormError'
import { useFormValidationFeedback } from '~/composables/ui/useFormValidationFeedback'
import { useDiscardUnsavedGuard } from '~/composables/ui/useDiscardUnsavedGuard'
import { useFormDirtySnapshot } from '~/composables/ui/useFormDirtySnapshot'
import { Domaine } from '~/models/Domaine'
import { useOrgEditActor } from '~/composables/auth/useOrgEditActor'
import { actorMayModifyDomaine } from '~~/utils/auth/orgEditAffordances'

type DomaineItem = {
  domLib: string
  domLibLong?: string | null
  domDesc?: string | null
}

type Props = {
  showOverlay?: boolean
  directionName: string
  domaines: DomaineItem[]
  isEditing?: boolean
  /** Quand fourni, pré-sélectionne ce domaine et charge ses données (depuis l’API) pour modification */
  domaineToEdit?: string
}

const props = withDefaults(defineProps<Props>(), {
  showOverlay: false,
  isEditing: false,
  domaineToEdit: '',
})

const emit = defineEmits<{
  (e: 'close' | 'refetch'): void
}>()

const { actor } = useOrgEditActor()
const mayDeleteDomaine = computed(() =>
  actorMayModifyDomaine(actor.value, props.directionName, props.domaineToEdit),
)

const hasUnsavedChanges = defineModel<boolean>('hasUnsavedChanges', { default: false })

const { storeDomaine, mergeDomaine, fetchDomaineById, deleteDomaine } = useDomaines()
const concurrent = useConcurrentForm()
const toast = useSourceToast()
const feedback = useFormValidationFeedback({
  fieldOrder: ['selectedDomaineName', 'domLib', 'domLibLong', 'domDesc'],
})

const loading = ref(false)
const loadDone = ref(false)
const showDeleteConfirm = ref(false)

const selectedDomaineName = ref('')

const domaineData = ref({
  dirLib: props.directionName,
  domLib: '',
  domLibLong: '',
  domDesc: '',
})

const formSnapshot = computed(() => ({
  ...domaineData.value,
  selectedDomaineName: selectedDomaineName.value,
}))

const showOverlayRef = computed(() => props.showOverlay)

const { captureSnapshot } = useFormDirtySnapshot({
  showOverlay: showOverlayRef,
  currentValue: formSnapshot,
  hasUnsavedChanges,
})

const domainesList = computed(() => props.domaines.map(d => d.domLib))

const resetForm = () => {
  feedback.clearAll()
  selectedDomaineName.value = ''
  loadDone.value = false
  domaineData.value = {
    dirLib: props.directionName,
    domLib: '',
    domLibLong: '',
    domDesc: '',
  }
}

watch(
  () => props.showOverlay,
  (show) => {
    if (!show || props.isEditing) return
    captureSnapshot()
  },
)

watch(
  () => [props.directionName, props.domaineToEdit, props.showOverlay] as const,
  async ([dir, toEdit, show]) => {
    if (!props.isEditing || !toEdit || !show) return
    selectedDomaineName.value = toEdit
    const id = `${dir}~${toEdit}`
    try {
      const res = await fetchDomaineById(id)
      if (res?.domaine) {
        const d = res.domaine
        domaineData.value = {
          dirLib: d.dirLib ?? props.directionName,
          domLib: d.domLib ?? '',
          domLibLong: d.domLibLong ?? '',
          domDesc: d.domDesc ?? '',
        }
        concurrent.rememberBase({ ...domaineData.value })
      }
    } finally {
      loadDone.value = true
      captureSnapshot()
    }
  },
  { immediate: true },
)

watch(
    selectedDomaineName,
    (newVal) => {
      if (!props.isEditing) return

      if (!newVal) {
        domaineData.value = {
          dirLib: props.directionName,
          domLib: '',
          domLibLong: '',
          domDesc: '',
        }
        return
      }

      const selected = props.domaines.find(d => d.domLib === newVal)
      if (!selected) return

      domaineData.value = {
        dirLib: props.directionName,
        domLib: selected.domLib ?? '',
        domLibLong: selected.domLibLong ?? '',
        domDesc: selected.domDesc ?? '',
      }
      concurrent.rememberBase({ ...domaineData.value })
    },
    { immediate: true },
)

function discardAndClose() {
  hasUnsavedChanges.value = false
  emit('close')
  resetForm()
  hasUnsavedChanges.value = false
}

const { showDiscardConfirm, requestClose, confirmDiscard, cancelDiscard } = useDiscardUnsavedGuard({
  isDirty: computed(() => hasUnsavedChanges.value),
  onDiscard: discardAndClose,
  loading,
})

function normalizeDomaineFields(): void {
  domaineData.value.domLib = normalizeString(domaineData.value.domLib)
  domaineData.value.domLibLong = normalizeString(domaineData.value.domLibLong) || ''
  domaineData.value.domDesc = normalizeString(domaineData.value.domDesc) || ''
}

function assertDomaineClientValid(): boolean {
  if (props.isEditing && !selectedDomaineName.value) {
    feedback.setFieldError('selectedDomaineName', 'Veuillez sélectionner un domaine à modifier.')
    feedback.notifySubmitFailure('client')
    feedback.focusFirstInvalid()
    return false
  }
  if (!domaineData.value.domLib) {
    feedback.setFieldError('domLib', "L'acronyme du domaine est obligatoire.")
    feedback.notifySubmitFailure('client')
    feedback.focusFirstInvalid()
    return false
  }
  return true
}

async function persistEditedDomaine(): Promise<void> {
  const origin = concurrent.savedBase.value
  const key = `${props.directionName}~${String(origin?.domLib ?? selectedDomaineName.value)}`
  const envelope = concurrent.buildPatch(domaineData.value, ['dirLib', 'domLib', 'domLibLong', 'domDesc'])
  if (envelope === null) {
    toast.success('Domaine modifié', `« ${domaineData.value.domLib} » a été mis à jour.`)
    return
  }
  const saved = await mergeDomaine(key, envelope.changes, envelope.base)
  const renamed = await concurrent.noteParentRename(key, saved?.currentKey, envelope.changes)
  if (!renamed) {
    toast.success('Domaine modifié', `« ${domaineData.value.domLib} » a été mis à jour.`)
  }
}

async function persistCreatedDomaine(): Promise<void> {
  await storeDomaine(new Domaine(domaineData.value))
  toast.success('Domaine créé', `« ${domaineData.value.domLib} » a été ajouté.`)
}

function handleDomaineSubmitError(error: unknown): void {
  if (concurrent.captureError(error)) return
  if (feedback.applyFromUnknown(error)) {
    feedback.notifySubmitFailure('api')
    feedback.focusFirstInvalid()
    return
  }
  feedback.notifyNonFieldError('Enregistrement impossible', getErrorMessage(error))
}

function confirmConflict(): void {
  concurrent.acceptChoices(domaineData.value)
  void validateForm()
}

function restoreFromTrash(): void {
  concurrent.closeConflict()
  void navigateTo('/admin/organigram-trash')
}

async function submitDomaineForm(): Promise<void> {
  normalizeDomaineFields()
  if (!assertDomaineClientValid()) return
  await (props.isEditing ? persistEditedDomaine() : persistCreatedDomaine())
  emit('refetch')
  discardAndClose()
}

async function validateForm() {
  feedback.clearAll()
  loading.value = true
  try {
    await submitDomaineForm()
  } catch (e: unknown) {
    handleDomaineSubmitError(e)
  } finally {
    loading.value = false
  }
}

const domaineToDeleteKey = computed(() => {
  const name = props.domaineToEdit || selectedDomaineName.value
  if (!name) return ''
  return `${props.directionName}~${name}`
})

const domaineDeleteConfirmPhrase = computed(() => {
  return domaineData.value.domLib || selectedDomaineName.value || props.domaineToEdit || ''
})

async function onConfirmDelete() {
  if (!domaineToDeleteKey.value) return
  feedback.clearAll()
  loading.value = true
  try {
    const ok = await deleteDomaine(domaineToDeleteKey.value)
    if (ok) {
      showDeleteConfirm.value = false
      toast.success('Domaine supprimé', 'Le domaine a été retiré de l’organigramme.')
      emit('refetch')
      discardAndClose()
    } else {
      feedback.notifyNonFieldError('Suppression impossible', 'Impossible de supprimer le domaine.')
    }
  } catch (e: unknown) {
    feedback.notifyNonFieldError('Suppression impossible', getErrorMessage(e))
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="form-content">
    <template v-if="isEditing && !!domaineToEdit && !loadDone">
      <EntityFormSkeleton label="Chargement du domaine" :input-count="2" />
    </template>
    <template v-else>
    <div class="form-body">
    <p class="info-text">
      {{ isEditing ? "Modification du domaine" : "Création d’un nouveau domaine" }} dans la direction :
      <span class="highlight">{{ directionName }}</span>
    </p>

    <div class="content">
      <SourceSelect
          v-if="isEditing && !domaineToEdit"
          name="selectedDomaineName"
          label="Sélectionner un domaine à modifier"
          :options="domainesList"
          size="medium"
          :error="feedback.fieldError('selectedDomaineName')"
          @update:selected-option="(val) => { selectedDomaineName = val; feedback.clearFieldError('selectedDomaineName') }"
      />

      <SourceInput
          :model-value="domaineData.domLib"
          name="domLib"
          label="Acronyme du domaine *"
          placeholder="Entrez l'acronyme"
          size="medium"
          type="text"
          :error="feedback.fieldError('domLib')"
          @update:model-value="(v) => { domaineData.domLib = v; feedback.clearFieldError('domLib') }"
      />

      <SourceInput
          :model-value="domaineData.domLibLong"
          name="domLibLong"
          label="Nom du domaine"
          placeholder="Entrez le nom"
          size="medium"
          type="text"
          :error="feedback.fieldError('domLibLong')"
          @update:model-value="(v) => { domaineData.domLibLong = v; feedback.clearFieldError('domLibLong') }"
      />

      <SourceRichTextEditor
          :model-value="domaineData.domDesc"
          name="domDesc"
          label="Description"
          placeholder="Ajoutez une description"
          size="medium"
          :error="feedback.fieldError('domDesc')"
          @update:model-value="(v) => { domaineData.domDesc = v; feedback.clearFieldError('domDesc') }"
      />
    </div>
    </div>

    <EntityFormFooter
      :show-delete="isEditing && mayDeleteDomaine"
      :delete-disabled="loading || !domaineToDeleteKey"
      :cancel-disabled="loading"
      :submit-disabled="loading || (!!domaineToEdit && !loadDone)"
      :submit-label="isEditing ? 'Enregistrer' : 'Créer'"
      @delete="showDeleteConfirm = true"
      @cancel="requestClose"
      @submit="validateForm"
    />
    </template>

    <DestructiveConfirmDialog
      v-model:open="showDeleteConfirm"
      title="Supprimer le domaine"
      :description="`Êtes-vous sûr de vouloir supprimer le domaine « ${domaineDeleteConfirmPhrase} » ? Cette action est irréversible.`"
      :confirm-phrase="domaineDeleteConfirmPhrase"
      :loading="loading"
      @confirm="onConfirmDelete"
    />

    <OrganizationConflictDialog
      :open="concurrent.conflict.value !== null || concurrent.deletedOpen.value"
      :deleted="concurrent.deletedOpen.value"
      :fields="concurrent.conflict.value?.fields ?? []"
      :choices="concurrent.choices.value"
      @choose="(field, choice) => { concurrent.choices.value[field] = choice }"
      @confirm="confirmConflict"
      @close="concurrent.closeConflict()"
      @restore="restoreFromTrash"
    />

    <DiscardUnsavedDialog
      :show="showDiscardConfirm"
      @close="cancelDiscard"
      @confirm="confirmDiscard"
    />
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/components/entity-form" as *;
</style>
